/**
 * Hero Video Slider (E20-1885)
 *
 * The Hero Slider with a looping video in each slide's media half. It extends
 * <hero-slider> (bfx-hero-slider.js, loaded first) for the track, swipe, dots, arrows,
 * arrow keys and the per-slide controls theme, and changes three things:
 *   - Slides never advance on their own; the shopper moves them.
 *   - Only the active slide's video plays (muted, looping), and only while the slider is
 *     on screen and the tab is visible. Videos are preload='none', so a slide's video downloads nothing until the
 *     track settles on its slide; slides passed on the way to another never load. Each
 *     poster covers its video until the first frame plays, then fades out.
 *   - The pause/play control stops the video, not the slideshow, and stays paused across
 *     slides. It starts paused, with every slide on its poster, for shoppers who asked
 *     for less motion or less data (reduced motion, Save-Data, a 2G connection), and
 *     pauses if reduced motion is turned on while the page is open.
 */
// Extends the registered element rather than naming the HeroSlider class: these files run
// as classic scripts sharing one global scope, so a local binding of that name would clash.
class HeroVideoSlider extends customElements.get('hero-slider') {
  connectedCallback() {
    super.connectedCallback();
    if (!this.track) return;

    this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.videoPaused = this.startsPaused();
    this.inView = false;
    this.settleTimer = null;

    const { signal } = this.abortController;

    // Sync the videos only once the track stops moving. A dot jump or the wrap from last to
    // first scrolls through the slides in between, and syncing mid-scroll would start (and
    // download) a video the shopper is only passing.
    this.track.addEventListener(
      'scroll',
      () => {
        clearTimeout(this.settleTimer);
        this.settleTimer = setTimeout(() => this.syncVideos(), 150);
      },
      { passive: true, signal }
    );

    this.querySelectorAll('[data-slider-video]').forEach((video) => {
      video.addEventListener(
        'playing',
        () => video.closest('.hero-slider__media')?.classList.add('is-started'),
        { signal }
      );
    });

    // Nothing plays (or downloads) until the slider is on screen, and it pauses once
    // scrolled out of view. Entries can batch oldest-first (during a resize, say), so
    // only the newest one reflects where the slider is now.
    this.observer = new IntersectionObserver((entries) => {
      this.inView = entries.at(-1).isIntersecting;
      this.syncVideos();
    });
    this.observer.observe(this);

    // The observer doesn't report a hidden tab, so pause while the page is hidden and resume
    // when it's back. (The base class's own listener only holds the slideshow timer.)
    document.addEventListener('visibilitychange', () => this.syncVideos(), { signal });

    // Turning on reduced motion mid-visit pauses the video as if the shopper had pressed
    // pause. Turning it back off leaves it paused: the preference only ever stops motion, so
    // it never resumes a video the shopper paused, and Play is one click away.
    this.motionQuery.addEventListener(
      'change',
      () => {
        if (!this.motionQuery.matches) return;
        this.videoPaused = true;
        this.syncVideos();
      },
      { signal }
    );

    this.syncToggle();
  }

  // Start on the posters, with Play offered, when the shopper asked for less motion or
  // less data. Save-Data and the connection type come from the Network Information API,
  // which only Chromium browsers expose; elsewhere they're undefined and the video plays.
  startsPaused() {
    const { connection } = navigator;
    return (
      this.motionQuery.matches ||
      connection?.saveData === true ||
      ['slow-2g', '2g'].includes(connection?.effectiveType)
    );
  }

  // Slides never advance on their own, so the base autoplay timer never starts.
  canPlay() {
    return false;
  }

  // Play the active slide's video and pause the rest. Inactive slides are rewound too: this
  // runs once the track has settled, so they're fully off screen and the jump is never seen,
  // and returning to one plays it from the start.
  syncVideos() {
    const active = this.activeIndex();

    this.slides.forEach((slide, i) => {
      const video = slide.querySelector('[data-slider-video]');
      if (!video) return;

      if (i === active && this.inView && !document.hidden && !this.videoPaused) {
        if (video.paused) this.playVideo(video);
        return;
      }

      if (!video.paused) video.pause();
      if (i !== active && video.currentTime > 0) video.currentTime = 0;
    });

    this.syncToggle();
  }

  playVideo(video) {
    video.play()?.catch((error) => {
      // AbortError is expected: scrolling away pauses a video that's still loading, and its
      // poster stays up. Any other rejection means the video isn't playing, so show the
      // paused state and let the button offer Play, which retries in one click. For a
      // NotAllowedError (the browser blocked autoplay, iOS Low Power Mode for one) that click
      // is the user gesture it needs; anything else, like an unsupported source, is logged.
      if (error?.name === 'AbortError') return;
      if (error?.name !== 'NotAllowedError') {
        console.warn('[bfx-hero-video-slider] a slide video failed to play:', error);
      }
      this.videoPaused = true;
      this.syncToggle();
    });
  }

  togglePlay() {
    this.videoPaused = !this.videoPaused;
    this.syncToggle();
    this.syncVideos();
  }

  // The label describes the action (Play when paused, Pause when playing), matching the
  // site's video toggle, so no aria-pressed (docs/dev/architecture/video-playback-toggle.md).
  // On a slide without a video there's nothing to pause, so the button steps out of the way.
  syncToggle() {
    this.classList.toggle('hero-slider--paused', this.videoPaused);
    if (!this.toggleBtn) return;
    const label = this.videoPaused
      ? this.toggleBtn.dataset.labelPlay
      : this.toggleBtn.dataset.labelPause;
    if (label) this.toggleBtn.setAttribute('aria-label', label);
    const activeHasVideo = Boolean(
      this.slides[this.activeIndex()]?.querySelector('[data-slider-video]')
    );
    this.toggleBtn.classList.toggle('is-unavailable', !activeHasVideo);
  }

  disconnectedCallback() {
    clearTimeout(this.settleTimer);
    this.observer?.disconnect();
    super.disconnectedCallback();
  }
}

if (!customElements.get('hero-video-slider')) {
  customElements.define('hero-video-slider', HeroVideoSlider);
}
